import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { DeleteButton, List, SaveButton, useDataGrid } from "@refinedev/mui";
import type { HttpError } from "@refinedev/core";
import { useModalForm } from "@refinedev/react-hook-form";
import { Controller } from "react-hook-form";
import { useNavigate } from "react-router";

import { CREATION_KINDS, formatDateTime, kindColor } from "./kinds";

// Mirrors the server's SETTABLE_STATUSES whitelist
// (admin-creations.controller.ts). Keep the two in step: a value offered here
// that the server does not accept is a 400 on save.
//
// The in-flight `generating_*` phases are deliberately absent — the server
// refuses them, because a story parked in a phase no run is executing gets
// picked up by the stuck-generation reaper and marked failed. A record already
// sitting in one still renders: see `statusOptionsFor`.
const STATUS_OPTIONS = [
  "draft",
  "constructing_story",
  "constructing_summary",
  "summaries_ready",
  "story_ready",
  "world_ready",
  "unlisted",
  "published",
  "hidden",
  "error_summaries",
  "error_story",
  "error_assets",
  "refining",
  "error",
] as const;

// A record can carry a status the admin may not ASSIGN (any `generating_*`, or
// a legacy value). Dropping it from the menu is what made the select render
// blank, so the current value is appended when it is not already an option —
// it can be shown and moved away from, just not chosen.
const statusOptionsFor = (current?: string): readonly string[] =>
  current && !STATUS_OPTIONS.includes(current as (typeof STATUS_OPTIONS)[number])
    ? [...STATUS_OPTIONS, current]
    : STATUS_OPTIONS;

type CreationRecord = {
  id: number;
  storyId: number;
  userId: number;
  title: string;
  description: string;
  visualStyle: string;
  genre: string;
  targetAudience: string;
  contentLevel: string;
  status: string;
  coverImageUrl: string | null;
  settings: unknown;
  createdAt: string;
  updatedAt: string;
};

// Only the content fields are editable; ids and timestamps are shown read-only.
type CreationFormValues = {
  title: string;
  description: string;
  visualStyle?: string;
  genre?: string;
  targetAudience?: string;
  contentLevel?: string;
  status?: string;
  coverImageUrl: string | null;
  settings: unknown;
};

// Text fields laid out as a 2-column CSS grid in the modal; `fullWidth` spans
// both columns (a CDN URL is unreadable in half a row).
//
// None is marked required. The server's patch schema is partial and real rows
// carry a blank targetAudience / contentLevel, so requiring them made
// exactly those rows impossible to save — including to correct their status.
const TEXT_FIELDS: {
  name: keyof CreationFormValues;
  label: string;
  fullWidth?: boolean;
}[] = [
  { name: "genre", label: "Genre" },
  { name: "visualStyle", label: "Visual Style" },
  { name: "targetAudience", label: "Target Audience" },
  { name: "contentLevel", label: "Content Level" },
  { name: "coverImageUrl", label: "Cover Image URL", fullWidth: true },
];

// The server patches partially but rejects an empty string on these fields
// (`z.string().trim().min(1)`), so a blank one is sent as `undefined` — which
// JSON.stringify drops from the body — rather than as "", which 400s. A value
// that is already set therefore cannot be cleared from here: the API has no
// representation for "unset this".
const optionalText = (value: unknown): string | undefined => {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed === "" ? undefined : trimmed;
};

const statusColor = (
  status: string
): "default" | "info" | "success" | "warning" | "error" => {
  if (status.startsWith("error")) {
    return "error";
  }
  if (status.startsWith("generating") || status.startsWith("constructing")) {
    return "info";
  }
  switch (status) {
    case "world_ready":
    case "published":
    case "unlisted":
      return "success";
    case "summaries_ready":
    case "story_ready":
    case "refining":
      return "info";
    case "hidden":
      return "warning";
    case "draft":
    default:
      return "default";
  }
};

export const CreationList = () => {
  const { dataGridProps, filters, setFilters } = useDataGrid({});
  const navigate = useNavigate();
  const kindFilter = filters.find((f) => "field" in f && f.field === "kind")?.value ?? "";

  const {
    modal: { visible, close, show },
    refineCore: { onFinish, formLoading, query },
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors },
    saveButtonProps,
  } = useModalForm<CreationRecord, HttpError, CreationFormValues>({
    refineCoreProps: { action: "edit", resource: "admin/creations" },
  });

  const record = query?.data?.data;

  // Parse the (possibly hand-edited) settings JSON, then send only the
  // editable fields — ids/timestamps are never included in the payload.
  const onSubmit = handleSubmit(async (values) => {
    let settings: unknown = values.settings;
    if (typeof settings === "string") {
      const raw = settings.trim();
      if (raw === "") {
        settings = null;
      } else {
        try {
          settings = JSON.parse(raw);
        } catch {
          setError("settings", { type: "manual", message: "Invalid JSON" });
          return;
        }
      }
    }

    const coverImageUrl = values.coverImageUrl?.toString().trim();

    try {
      await onFinish({
        title: values.title,
        description: values.description,
        genre: optionalText(values.genre),
        visualStyle: optionalText(values.visualStyle),
        targetAudience: optionalText(values.targetAudience),
        contentLevel: optionalText(values.contentLevel),
        // Omitted when unchanged. The record may sit in a status the server
        // refuses to be ASSIGNED (a `generating_*` phase); echoing it back
        // would 400 the whole patch and block editing every other field.
        status: values.status === record?.status ? undefined : values.status,
        coverImageUrl: coverImageUrl ? coverImageUrl : null,
        settings,
      });
      close();
    } catch {
      // Refine surfaces the server error via a notification; keep the modal open.
    }
  });

  const columns = React.useMemo<GridColDef[]>(
    () => [
      {
        field: "title",
        headerName: "Title",
        flex: 1.4,
        minWidth: 200,
        display: "flex",
      },
      {
        field: "kind",
        headerName: "Kind",
        minWidth: 100,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => <Chip size="small" label={row.kind} color={kindColor(row.kind)} />,
      },
      {
        // A Story Studio item's own id inside its creator's library; a v1 story has none.
        field: "itemId",
        headerName: "Item ID",
        minWidth: 110,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (row.itemId ? <code>{row.itemId}</code> : "-"),
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 130,
        display: "flex",
        // A Story Studio item has no lifecycle: its status column is the stand-in "v2".
        renderCell: ({ row }) =>
          row.kind === "story" ? <Chip size="small" label={row.status} color={statusColor(row.status)} /> : "-",
      },
      {
        field: "userId",
        headerName: "User",
        minWidth: 140,
        display: "flex",
        renderCell: ({ row }) =>
          row.userName ? `${row.userId} (${row.userName})` : row.userId,
      },
      {
        field: "genre",
        headerName: "Genre",
        minWidth: 120,
        display: "flex",
      },
      {
        field: "visualStyle",
        headerName: "Visual Style",
        minWidth: 130,
        display: "flex",
      },
      {
        field: "targetAudience",
        headerName: "Audience",
        minWidth: 120,
        display: "flex",
      },
      {
        field: "contentLevel",
        headerName: "Content",
        minWidth: 100,
        display: "flex",
      },
      {
        field: "storyId",
        headerName: "Story ID",
        minWidth: 110,
        display: "flex",
        renderCell: ({ row }) => (row.kind === "story" ? <code>{row.storyId}</code> : "-"),
      },
      {
        field: "updatedAt",
        headerName: "Updated",
        minWidth: 180,
        display: "flex",
        valueFormatter: (value) => formatDateTime(value as string | null | undefined),
      },
      {
        field: "createdAt",
        headerName: "Created",
        minWidth: 180,
        display: "flex",
        valueFormatter: (value) => formatDateTime(value as string | null | undefined),
      },
      {
        field: "actions",
        headerName: "Actions",
        align: "right",
        headerAlign: "right",
        minWidth: 120,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="View JSON">
              <IconButton size="small" onClick={() => navigate(`/creations/show/${row.id}`)}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {/* A Story Studio item is read-only here: the server answers 409 to an edit or a delete. */}
            {row.kind === "story" ? (
              <>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => show(row.id)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <DeleteButton hideText recordItemId={row.id} />
              </>
            ) : null}
          </Stack>
        ),
      },
    ],
    [show, navigate]
  );

  return (
    <List canCreate={false}>
      <TextField
        select
        size="small"
        label="Kind"
        value={kindFilter}
        onChange={(e) =>
          setFilters(e.target.value ? [{ field: "kind", operator: "eq", value: e.target.value }] : [], "replace")
        }
        sx={{ mb: 2, minWidth: 180 }}
      >
        <MenuItem value="">All kinds</MenuItem>
        {CREATION_KINDS.map((kind) => (
          <MenuItem key={kind} value={kind}>
            {kind}
          </MenuItem>
        ))}
      </TextField>
      <DataGrid {...dataGridProps} columns={columns} />

      <Dialog open={visible} onClose={close} fullWidth maxWidth="md">
        <DialogTitle>
          Edit Creation
          {record ? ` · #${record.id}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {record ? (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
              id {record.id} · storyId {record.storyId} · userId {record.userId} ·
              created {formatDateTime(record.createdAt)} · updated{" "}
              {formatDateTime(record.updatedAt)}
            </Typography>
          ) : null}

          <Box component="form" sx={{ display: "flex", flexDirection: "column" }} autoComplete="off">
            <TextField
              {...register("title", { required: "Title is required" })}
              error={!!errors?.title}
              helperText={errors?.title?.message?.toString()}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              label="Title"
            />

            {/*
              Controlled, unlike the single-line fields above. An uncontrolled
              multiline TextField is measured once, while the dialog is still
              transitioning in, and never re-renders afterwards — so the
              autosize textarea locks in at `minRows` and clips the rest of the
              text. Driving it through `Controller` re-renders it on every
              keystroke and on the initial load, which lets it size to content.
            */}
            <Controller
              control={control}
              name="description"
              rules={{ required: "Description is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ""}
                  error={!!errors?.description}
                  helperText={errors?.description?.message?.toString()}
                  margin="normal"
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={12}
                  InputLabelProps={{ shrink: true }}
                  label="Description"
                />
              )}
            />

            <Controller
              control={control}
              name="status"
              rules={{ required: "Status is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ""}
                  select
                  margin="normal"
                  fullWidth
                  label="Status"
                  error={!!errors?.status}
                  helperText={errors?.status?.message?.toString()}
                >
                  {statusOptionsFor(record?.status).map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            {/*
              A CSS grid, NOT a wrapping `<Stack direction="row" flexWrap>`.
              Stack's default (`useFlexGap={false}`) spacing emits
              `& > :not(style):not(style) { margin: 0 }` plus a `margin-left` on
              every child after the first. That did two visible things here: it
              wiped the `margin="normal"` off all six fields, leaving wrapped
              rows with ZERO vertical gutter so each row's floating label was
              struck through by the outline of the field above it; and it
              indented every row after the first by the leftover `margin-left`.
              Grid `gap` spaces both axes and indents nothing.
            */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                gap: 2,
                mt: 2,
              }}
            >
              {TEXT_FIELDS.map(({ name, label, fullWidth }) => (
                <TextField
                  key={name}
                  {...register(name)}
                  error={!!errors?.[name]}
                  helperText={errors?.[name]?.message?.toString()}
                  sx={fullWidth ? { gridColumn: { sm: "1 / -1" } } : undefined}
                  InputLabelProps={{ shrink: true }}
                  label={label}
                />
              ))}
            </Box>

            <Controller
              control={control}
              name="settings"
              render={({ field }) => (
                <TextField
                  value={
                    typeof field.value === "string"
                      ? field.value
                      : field.value == null
                        ? ""
                        : JSON.stringify(field.value, null, 2)
                  }
                  onChange={(e) => field.onChange(e.target.value)}
                  error={!!errors?.settings}
                  helperText={
                    errors?.settings?.message?.toString() ??
                    "Raw generation-config JSON (leave blank to clear)"
                  }
                  margin="normal"
                  fullWidth
                  multiline
                  minRows={3}
                  InputLabelProps={{ shrink: true }}
                  label="Settings (JSON)"
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <SaveButton {...saveButtonProps} onClick={onSubmit} disabled={formLoading} />
        </DialogActions>
      </Dialog>
    </List>
  );
};
